import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import AppUrl from '../../restApi/AppUrl';
import IRestClient from '../../restApi/IRestClient';
import ReactHtmlParser from 'react-html-parser';
import { motion, AnimatePresence } from 'framer-motion';
import SectionHeader from '../SectionHeader/SectionHeader';
import CountUp from 'react-countup';
import VisibilitySensor from 'react-visibility-sensor';
import {
  FaHandsHelping, FaBullseye, FaLightbulb,
  FaArrowRight, FaQuoteLeft, FaHandHoldingHeart,
  FaHeart, FaUsers, FaGlobe, FaCheckCircle,
  FaHome, FaUtensils, FaStar, FaAward,
  FaImage,
} from 'react-icons/fa';
import aboutFallback from '../../img/about.jpg';

/* ── Skeleton block ──────────────────────────────────────── */
const Sk = ({ className = '' }) => (
  <div className={`animate-pulse bg-neutral-200 rounded-xl ${className}`} />
);

/* ── Tab config ──────────────────────────────────────────── */
const TABS = [
  { id: 'about',   label: 'Our Story', Icon: FaHandsHelping },
  { id: 'mission', label: 'Mission',   Icon: FaBullseye     },
  { id: 'vision',  label: 'Vision',    Icon: FaLightbulb    },
];

const TAB_TITLE = {
  about:   'Making a Difference Since 2019',
  mission: 'Our Core Mission',
  vision:  'Our Vision for the Future',
};

/* ── Stat icon mapper ────────────────────────────────────── */
const getCountIcon = (name = '') => {
  const n = name.toLowerCase();
  if (n.includes('people') || n.includes('refugee') || n.includes('youth') || n.includes('family')) return FaUsers;
  if (n.includes('donation') || n.includes('help') || n.includes('support')) return FaHandHoldingHeart;
  if (n.includes('country') || n.includes('nation') || n.includes('global') || n.includes('communit')) return FaGlobe;
  if (n.includes('home') || n.includes('house') || n.includes('shelter')) return FaHome;
  if (n.includes('food') || n.includes('meal') || n.includes('nutrition')) return FaUtensils;
  if (n.includes('love') || n.includes('care') || n.includes('compassion')) return FaHeart;
  if (n.includes('year') || n.includes('star') || n.includes('excellent')) return FaStar;
  if (n.includes('award') || n.includes('recognition')) return FaAward;
  return FaUsers;
};

const STAT_COLORS = [
  { bg: '#E8F8EE', icon: '#3DAA6B', num: '#237A4B' }, // brand green
  { bg: '#EEF1F9', icon: '#1B2B5C', num: '#142147' }, // brand navy
  { bg: '#E8F8EE', icon: '#3DAA6B', num: '#237A4B' }, // brand green (cycle)
  { bg: '#E8F8EE', icon: '#3DAA6B', num: '#237A4B' }, // brand green (cycle)
];

const FALLBACK_STATS = [
  { Icon: FaUsers, label: 'Youth Reached',    value: 500, suffix: '+' },
  { Icon: FaGlobe, label: 'Communities',      value: 15,  suffix: '+' },
  { Icon: FaStar,  label: 'Years of Service', value: 5,   suffix: '+' },
];

/* ═══════════════════════════════════════════════════════════
   Aboutshome — Homepage teaser (no Core Values / Objectives)
═══════════════════════════════════════════════════════════ */
class Aboutshome extends Component {
  constructor() {
    super();
    this.state = {
      about:            '',
      mission:          '',
      vision:           '',
      whatwhywedo:      '',
      aboutImage:       '',   // desktop: 4:5 portrait  (800×1000)
      aboutImageMobile: '',   // mobile : 3:2 landscape (900×600)
      activeTab:        'about',
      countData:        [],
      loadingAbout:     true,
      loadingCount:     true,
      imgLoaded:        false,
      imgError:         false,
    };
  }

  componentDidMount() {
    IRestClient.GetRequest(AppUrl.Abouthome).then(result => {
      if (result && result[0]) {
        this.setState({
          about:        result[0]['about']        || '',
          mission:      result[0]['mission']      || '',
          vision:       result[0]['number']       || result[0]['vision'] || '',
          whatwhywedo:  result[0]['whatwhywedo']  || '',
          aboutImage:       result[0]['about_image2']       || '',
          aboutImageMobile: result[0]['about_image_mobile'] || '',
          loadingAbout:     false,
        });
      } else {
        this.setState({ loadingAbout: false });
      }
    }).catch(() => this.setState({ loadingAbout: false }));

    IRestClient.GetRequest(AppUrl.Counthome).then(result => {
      this.setState({ countData: result || [], loadingCount: false });
    }).catch(() => this.setState({ loadingCount: false }));
  }

  handleTabChange = (tab) => this.setState({ activeTab: tab });

  render() {
    const {
      about, mission, vision, whatwhywedo,
      aboutImage, aboutImageMobile,
      activeTab, countData, loadingAbout, loadingCount,
      imgLoaded, imgError,
    } = this.state;

    // Desktop source: 4:5 portrait (800×1000) — fall back to local jpg
    const imgDesktop = aboutImage && !imgError ? aboutImage : aboutFallback;
    // Mobile source : 3:2 landscape (900×600) — fall back to desktop if not yet generated
    const imgMobile  = aboutImageMobile && !imgError ? aboutImageMobile : imgDesktop;
    const tabBody  = { about, mission, vision };
    const stats    = countData.length > 0 ? countData.slice(0, 3) : FALLBACK_STATS;
    const showSkel = !imgLoaded && aboutImage;   // skeleton only while API image loads

    return (
      <section className="py-14 sm:py-20 lg:py-24 bg-white overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* ═══════════════════════════════════════════════
              Two-column grid  |  mobile → single column
              Order on mobile: content first, image below
          ═══════════════════════════════════════════════ */}
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start lg:items-center">

            {/* ────────────────────────────────────────────
                LEFT  —  Image panel
                Desktop: portrait  |  Mobile: landscape
            ──────────────────────────────────────────── */}
            <motion.div
              className="relative order-2 lg:order-1"
              initial={{ opacity: 0, x: -44 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.72, ease: 'easeOut' }}>

              {/* Green accent spine — hidden on very small screens, shown sm+ */}
              <div
                className="hidden sm:block absolute -left-3 lg:-left-4 top-8 bottom-8 w-1.5 rounded-full z-20"
                style={{ background: 'linear-gradient(180deg,#237A4B 0%,#3DAA6B 55%,#94DDB8 100%)' }}
              />

              {/* Decorative orb — top-right */}
              <div
                className="absolute -top-5 -right-5 w-24 h-24 rounded-full opacity-[0.18] blur-2xl pointer-events-none"
                style={{ background: '#2E8B54' }}
              />

              {/* ── Image frame ────────────────────────────────────
                  Responsive aspect ratios (via scoped <style>):
                    mobile  <640px   : 16/9  — landscape, tight on phones
                    sm      640px+   : 4/3   — transitional
                    lg      1024px+  : 4/5   — portrait, matches the 600×750 server crop
              ─────────────────────────────────────────────── */}
              {/*
                  Responsive aspect ratios:
                    mobile  <640px   → 3:2  landscape  (matches 900×600 server crop)
                    sm      640px+   → 4:3  transitional
                    lg      1024px+  → 4:5  portrait   (matches 800×1000 server crop)
              */}
              <style>{`
                .about-img-frame { aspect-ratio: 3/2; }
                @media (min-width: 640px)  { .about-img-frame { aspect-ratio: 4/3; } }
                @media (min-width: 1024px) { .about-img-frame { aspect-ratio: 4/5; } }
              `}</style>

              <div className="about-img-frame relative rounded-2xl sm:rounded-3xl overflow-hidden shadow-xl sm:shadow-2xl sm:ml-2">

                {/* Skeleton while API image is loading */}
                {showSkel && (
                  <div
                    className="absolute inset-0 animate-pulse bg-neutral-200 z-10"
                    style={{ borderRadius: 'inherit' }}
                  />
                )}

                {/* ── Picture element — serves the right crop per device ── */}
                {/* Mobile (<640px): 3:2 landscape crop (900×600)            */}
                {/* Tablet + Desktop (640px+): 4:5 portrait crop (800×1000)  */}
                <picture
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block' }}>
                  <source media="(max-width: 639px)" srcSet={imgMobile} />
                  <img
                    src={imgDesktop}
                    alt="SOYAN team empowering youth"
                    loading="lazy"
                    decoding="async"
                    onLoad={() => this.setState({ imgLoaded: true })}
                    onError={() => this.setState({ imgError: true, imgLoaded: true })}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      /* portrait → anchors to top on any landscape crop  */
                      objectPosition: 'center top',
                      display: 'block',
                      opacity: imgLoaded || !aboutImage ? 1 : 0,
                      transition: 'opacity 0.45s ease',
                    }}
                  />
                </picture>

                {/* Error / no-image state */}
                {imgError && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-neutral-100 text-neutral-300 z-20">
                    <FaImage className="text-4xl mb-2" />
                    <span className="text-xs font-medium">Image unavailable</span>
                  </div>
                )}

                {/* Bottom gradient overlay */}
                <div
                  className="absolute inset-0 pointer-events-none z-10"
                  style={{ background: 'linear-gradient(to top, rgba(27,43,92,0.72) 0%, rgba(27,43,92,0.10) 42%, transparent 68%)' }}
                />

                {/* "Est. 2018" badge — top-right */}
                <motion.div
                  className="absolute top-3 right-3 sm:top-4 sm:right-4 z-20"
                  initial={{ opacity: 0, scale: 0.75 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.55, duration: 0.4, type: 'spring', stiffness: 220 }}>
                  <div
                    className="rounded-xl sm:rounded-2xl px-2.5 py-1.5 sm:px-3 sm:py-2 shadow-lg"
                    style={{ background: 'rgba(255,255,255,0.93)', backdropFilter: 'blur(10px)' }}>
                    <p className="text-[10px] font-black text-primary-700 uppercase tracking-widest leading-none">Est.</p>
                    <p className="text-base sm:text-lg font-black text-secondary-600 leading-none">2019</p>
                  </div>
                </motion.div>

                {/* Floating stat cards — bottom */}
                <motion.div
                  className="absolute bottom-3 left-3 right-3 sm:bottom-4 sm:left-4 sm:right-4 z-20"
                  initial={{ opacity: 0, y: 18 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.65, duration: 0.5 }}>
                  <div className="grid grid-cols-2 gap-2">
                    <div
                      className="rounded-xl sm:rounded-2xl p-2.5 sm:p-3 shadow-lg"
                      style={{ background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(10px)' }}>
                      <p className="text-lg sm:text-xl font-black text-primary-900 leading-none">500+</p>
                      <p className="text-[11px] sm:text-xs font-semibold mt-0.5 text-neutral-500">Youth Reached</p>
                    </div>
                    <div
                      className="rounded-xl sm:rounded-2xl p-2.5 sm:p-3 shadow-lg"
                      style={{ background: 'rgba(61,170,107,0.90)', backdropFilter: 'blur(10px)' }}>
                      <p className="text-lg sm:text-xl font-black text-white leading-none">15+</p>
                      <p className="text-[11px] sm:text-xs font-semibold mt-0.5 text-white/80">Communities</p>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Dot-grid — bottom-right decorative */}
              <div
                className="hidden sm:block absolute -bottom-4 -right-3 w-24 h-24 opacity-20 pointer-events-none"
                style={{
                  backgroundImage: 'radial-gradient(circle,#3DAA6B 1.5px,transparent 1.5px)',
                  backgroundSize: '9px 9px',
                }}
              />
            </motion.div>

            {/* ────────────────────────────────────────────
                RIGHT  —  Content panel
            ──────────────────────────────────────────── */}
            <motion.div
              className="order-1 lg:order-2 flex flex-col"
              initial={{ opacity: 0, x: 44 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.72, ease: 'easeOut', delay: 0.1 }}>

              {/* Section Header */}
              <SectionHeader
                badge="About SOYAN"
                title="Empowering Refugee Youth for a Better Tomorrow"
                accentWord="for a Better Tomorrow"
                subtitle="A youth-led non-profit empowering refugees and host communities through education, entrepreneurship, health, peacebuilding, and community development since 2019."
                variant="light"
                align="left"
                className="mb-5 sm:mb-6"
              />

              {/* ── Live stat strip ───────────────────── */}
              <motion.div
                className="grid grid-cols-3 gap-2 sm:gap-3 mb-6 sm:mb-7"
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}>
                {loadingCount
                  ? [0, 1, 2].map(i => <Sk key={i} className="h-[76px] rounded-2xl" />)
                  : stats.map((s, i) => {
                      const Icon  = s.count_name ? getCountIcon(s.count_name) : s.Icon;
                      const value = s.count_number != null ? parseInt(String(s.count_number)) || 0 : s.value;
                      const label = s.count_name || s.label;
                      const suf   = s.suffix || '+';
                      const col   = STAT_COLORS[i % STAT_COLORS.length];
                      return (
                        <div
                          key={i}
                          className="flex flex-col items-center text-center rounded-2xl py-3 sm:py-4 px-1 sm:px-2"
                          style={{ background: col.bg, border: '1px solid rgba(255,255,255,0.8)', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                          <Icon style={{ color: col.icon, fontSize: '0.95rem', marginBottom: '5px' }} />
                          <div className="flex items-baseline gap-px leading-none">
                            <CountUp start={0} end={value} duration={2.5}>
                              {({ countUpRef, start }) => (
                                <VisibilitySensor onChange={start} partialVisibility>
                                  <span
                                    ref={countUpRef}
                                    className="text-lg sm:text-xl font-black"
                                    style={{ color: col.num }}
                                  />
                                </VisibilitySensor>
                              )}
                            </CountUp>
                            <span className="text-base sm:text-lg font-black" style={{ color: col.num }}>{suf}</span>
                          </div>
                          <span className="text-[10px] sm:text-xs text-neutral-400 font-medium mt-1 leading-tight text-center">
                            {label}
                          </span>
                        </div>
                      );
                    })
                }
              </motion.div>

              {/* ── Tabs + content ────────────────────── */}
              <motion.div
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.28 }}>

                {loadingAbout ? (
                  <div className="space-y-3">
                    <div className="flex gap-1.5">
                      <Sk className="h-11 flex-1" />
                      <Sk className="h-11 flex-1" />
                      <Sk className="h-11 flex-1" />
                    </div>
                    <Sk className="h-40 w-full" />
                  </div>
                ) : (
                  <>
                    {/* Pill tab nav */}
                    <div className="flex bg-neutral-100 rounded-2xl p-1 mb-4 gap-1">
                      {TABS.map(({ id, label, Icon }) => (
                        <button
                          key={id}
                          onClick={() => this.handleTabChange(id)}
                          className={`flex-1 flex items-center justify-center gap-1 sm:gap-1.5 py-2.5 sm:py-3 px-1 sm:px-3 rounded-xl text-[11px] sm:text-sm font-semibold transition-all duration-300 ${
                            activeTab === id
                              ? 'bg-white shadow-md text-primary-800'
                              : 'text-neutral-500 hover:text-primary-700'
                          }`}>
                          <Icon
                            className={`text-xs sm:text-sm flex-shrink-0 ${activeTab === id ? 'text-secondary-600' : ''}`}
                          />
                          <span className="truncate">{label}</span>
                        </button>
                      ))}
                    </div>

                    {/* Tab content card */}
                    <AnimatePresence mode="wait">
                      <motion.div
                        key={activeTab}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.26 }}
                        className="bg-white rounded-2xl border border-neutral-100 shadow-lg p-4 sm:p-5 lg:p-6">

                        <FaQuoteLeft
                          className="mb-2"
                          style={{ color: '#94DDB8', fontSize: '1rem' }}
                        />

                        <h3 className="text-sm sm:text-base lg:text-lg font-bold text-primary-900 mb-2 leading-snug">
                          {TAB_TITLE[activeTab]}
                        </h3>

                        <div className="text-neutral-600 text-xs sm:text-sm leading-relaxed">
                          {activeTab === 'about' ? (
                            <>
                              {ReactHtmlParser((tabBody.about || '').substring(0, 280))}
                              {(tabBody.about || '').length > 280 && (
                                <span className="text-neutral-400">… </span>
                              )}
                              <Link
                                to="/about"
                                className="inline-flex items-center gap-1.5 font-semibold text-xs sm:text-sm mt-2 transition-all duration-200 group"
                                style={{ color: '#3DAA6B' }}>
                                Read Full Story
                                <FaArrowRight className="text-[10px] transition-transform duration-300 group-hover:translate-x-1" />
                              </Link>
                            </>
                          ) : (
                            <>
                              {ReactHtmlParser(tabBody[activeTab] || '')}
                              {activeTab === 'vision' && whatwhywedo && (
                                <div className="mt-2 pt-2 border-t border-neutral-100">
                                  {ReactHtmlParser(whatwhywedo)}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </motion.div>
                    </AnimatePresence>

                    {/* ── CTAs ─────────────────────────────── */}
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mt-5">
                      {/* Donate Now — hidden until payment goes live */}
                      {false && (
                        <Link
                          to="/donate"
                          className="inline-flex items-center justify-center gap-2 font-bold py-3 px-6 rounded-xl text-sm transition-all duration-300 shadow-md hover:shadow-lg hover:-translate-y-0.5 text-white"
                          style={{ background: '#237A4B' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#1B6039'}
                          onMouseLeave={e => e.currentTarget.style.background = '#237A4B'}>
                          <FaHandHoldingHeart /> Donate Now
                        </Link>
                      )}
                      <Link
                        to="/about"
                        className="inline-flex items-center justify-center gap-2 font-semibold text-sm border border-neutral-200 rounded-xl py-3 px-6 bg-white text-primary-700 transition-all duration-300 group hover:border-secondary-300 hover:text-secondary-700 hover:bg-secondary-50">
                        Learn About Us
                        <FaArrowRight className="text-xs transition-transform duration-300 group-hover:translate-x-1" />
                      </Link>
                    </div>
                  </>
                )}
              </motion.div>
            </motion.div>
          </div>{/* /grid */}
        </div>
      </section>
    );
  }
}

export default Aboutshome;
